import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from './supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in from localStorage
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (error) {
        console.error('Error loading user from localStorage:', error);
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const signUp = async (email, password, fullName) => {
    try {
      // Check if user already exists
      const { data: existingUser, error: checkError } = await supabase
        .from('users')
        .select('*')
        .eq('email', email)
        .single();

      if (existingUser) {
        return { error: { message: 'Email already registered. Please login instead.' } };
      }

      // Create new user
      const { data, error } = await supabase
        .from('users')
        .insert([
          {
            email: email,
            password: password, // In production, hash this password
            full_name: fullName,
            created_at: new Date().toISOString()
          }
        ])
        .select()
        .single();

      if (error) {
        return { error };
      }

      // Set user in state and localStorage
      const userData = {
        id: data.id,
        email: data.email,
        full_name: data.full_name
      };
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));

      return { data: { user: userData } };
    } catch (error) {
      return { error: { message: 'Failed to create account. Please try again.' } };
    }
  };

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('email', email)
        .eq('password', password)
        .single();

      if (error || !data) {
        console.error('Login error:', error);
        return { error: { message: 'Invalid email or password' } };
      }

      // Check if account is deleted
      if (data.deleted === true) {
        return { error: { message: 'This account has been deleted and cannot be accessed' } };
      }

      // Set user in state and localStorage
      const userData = {
        id: data.id,
        email: data.email,
        full_name: data.full_name
      };
      
      // Update state immediately
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      
      console.log('User logged in:', userData);

      return { data: { user: userData } };
    } catch (error) {
      console.error('Sign in error:', error);
      return { error: { message: 'Failed to sign in. Please try again.' } };
    }
  };

  const signOut = async () => {
    setUser(null);
    localStorage.removeItem('user');
    return { error: null };
  };

  const resetPassword = async (email) => {
    // Simple implementation - just return success
    // In production, you'd want to send an email with reset link
    return { data: { message: 'Password reset email sent' }, error: null };
  };

  const deleteAccount = async () => {
    try {
      if (!user) {
        return { error: { message: 'No user logged in' } };
      }

      // Mark user as deleted in database
      const { error } = await supabase
        .from('users')
        .update({ deleted: true })
        .eq('id', user.id);

      if (error) {
        console.error('Error deleting account:', error);
        return { error };
      }

      // Sign out and clear local storage
      setUser(null);
      localStorage.removeItem('user');

      return { error: null };
    } catch (error) {
      console.error('Delete account error:', error);
      return { error: { message: 'Failed to delete account. Please try again.' } };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signUp,
        signIn,
        signOut,
        resetPassword,
        deleteAccount,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
